/**
 * image-compare component
 *
 * Renders a before/after image slider on desktop (pointer: fine).
 * Interaction: hover to compare — the divider follows the mouse with no click required.
 * Falls back to the "after" image only on mobile/touch devices.
 *
 * Props:
 *   before  {string} - Path to the "before" image (old version, shown on right)
 *   after   {string} - Path to the "after" image (new version, shown on left)
 *
 * @param {object} props
 * @param {number} index - Panel index for stagger animation
 * @returns {HTMLElement}
 */

import './image-compare.css';
import { PANEL_ANIMATION_DELAY_MS } from '../constants.js';

const DESKTOP_QUERY = '(pointer: fine) and (min-width: 769px)';

export function createImageCompare(props, index) {
  const { before, after } = props;
  const isDesktop = window.matchMedia(DESKTOP_QUERY).matches;

  const panel = document.createElement('div');
  panel.className = 'portfolio-panel portfolio-panel--component';
  panel.classList.add('skeleton-loading');

  if (!isDesktop) return null;

  buildInteractive(panel, { before, after });
  setTimeout(() => panel.classList.add('animated'), PANEL_ANIMATION_DELAY_MS * (index || 0));
  return panel;
}

function buildInteractive(panel, { before, after }) {
  let ro = null;

  // Outer container
  const container = document.createElement('div');
  container.className = 'ic-container';

  // "After" (new) image — base layer on left, sets container height
  const imgBase = document.createElement('img');
  imgBase.className = 'ic-img';
  imgBase.alt = 'After redesign';
  imgBase.src = after;

  // "Before" (old) image — overlay on right, shrinks as mouse moves left
  const overlayWrapper = document.createElement('div');
  overlayWrapper.className = 'ic-after-wrapper';

  const imgOverlay = document.createElement('img');
  imgOverlay.className = 'ic-img';
  imgOverlay.alt = 'Before redesign';
  imgOverlay.src = before;

  overlayWrapper.appendChild(imgOverlay);

  // Divider line + handle
  const divider = document.createElement('div');
  divider.className = 'ic-divider';

  const handle = document.createElement('div');
  handle.className = 'ic-handle';
  handle.innerHTML =
    '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<path d="M7 5L2 10L7 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
    '<path d="M13 5L18 10L13 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';
  divider.appendChild(handle);

  container.appendChild(imgBase);
  container.appendChild(overlayWrapper);
  container.appendChild(divider);
  panel.appendChild(container);

  // --- Position logic ---
  function applyPosition(x, containerWidth) {
    imgOverlay.style.width = containerWidth + 'px';
    overlayWrapper.style.width = x + 'px';
    divider.style.left = x + 'px';
  }

  function initPosition() {
    const rect = container.getBoundingClientRect();
    if (rect.width === 0) return;
    applyPosition(rect.width / 2, rect.width);
    panel.classList.remove('skeleton-loading');
    if (ro) { ro.disconnect(); ro = null; }
  }

  // ResizeObserver handles the return-visit case (container was display:none at build time)
  ro = new ResizeObserver(initPosition);
  ro.observe(container);

  // Also init on image load in case ResizeObserver fires before images are decoded
  let loadedCount = 0;
  const onLoad = () => {
    loadedCount++;
    if (loadedCount >= 2) initPosition();
  };
  imgBase.onload = onLoad;
  imgBase.onerror = onLoad;
  imgOverlay.onload = onLoad;
  imgOverlay.onerror = onLoad;

  // --- Hover interaction: divider follows mouse, no click required ---
  container.addEventListener('mousemove', (e) => {
    const rect = container.getBoundingClientRect();
    const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
    applyPosition(x, rect.width);
  });

  // Reset to center when mouse leaves
  container.addEventListener('mouseleave', () => {
    const rect = container.getBoundingClientRect();
    applyPosition(rect.width / 2, rect.width);
  });
}
